import { useEffect, useState } from "react";
import { AlertTriangle, X } from "lucide-react";
import { api } from "../lib/api";
import { tokenStore } from "../lib/tokenStore";
import { hasCompletedPermissions } from "../pages/PermissionsPage";

/** Floating banner shown when the **logged-in** customer's saved default
 * address sits in an INACTIVE zone. Stays silent for anonymous visitors
 * (no banner before login + before they pick a location) — per product
 * spec: never spook a first-time visitor with a "we don't deliver here"
 * message before they've even told us where "here" is.
 *
 * Gating order (ALL must pass before we even hit the network):
 * 1. Not currently on an /onboarding/* or /legal/* route.
 *    App Store Guideline 5.1.1(iv) — we must NOT show ANY
 *    location-derived UI on the permissions-onboarding screen, because
 *    the reviewer reads it as "the app already knows where I am
 *    without my consent" (real Apple rejection notice, Jun 2026).
 * 2. The user has completed (or explicitly skipped) the permission
 *    onboarding. Before that point the app has no business making
 *    location-aware statements — same Apple-compliance rationale.
 * 3. There IS an auth token (anonymous home page stays clean).
 * 4. The logged-in customer has a SAVED address with coords/pincode.
 *    A saved address is an explicit, consented location signal — the
 *    customer typed it / picked it on the map themselves.
 *
 * Only then do we ask the backend whether the resolved zone is
 * currently accepting orders.
 */
export default function ZoneOfflineBanner() {
    const [state, setState] = useState({ show: false, msg: "" });
    const [dismissed, setDismissed] = useState(() => sessionStorage.getItem("bizil_zone_dismiss") === "1");

    useEffect(() => {
        let cancelled = false;

        // (1) Never show on onboarding / legal routes. The reviewer is
        // looking at "Quick setup" — they have not given any
        // location signal yet. Showing "Hyderabad currently not
        // accepting orders" before consent is the exact App Store
        // rejection scenario.
        const path = typeof window !== "undefined" ? window.location.pathname : "";
        if (path.startsWith("/onboarding/") || path.startsWith("/legal/") || path.startsWith("/login")) {
            return;
        }

        // (2) Wait until the permission-onboarding has run (granted
        // OR explicitly dismissed by the user). Before then we make
        // zero location-aware statements.
        if (!hasCompletedPermissions()) return;

        // (3) Hard-gate: anonymous visitors never see the banner.
        if (!tokenStore.get()) return;

        (async () => {
            try {
                // (4) Need a saved address. Customers without one are
                // still exploring — they'll pick a location at
                // checkout or via the address picker, and the banner
                // will surface there (and only there) if the area is
                // unserviceable.
                let lat, lng, pincode;
                try {
                    const { data } = await api.get("/addresses");
                    const def = (data || []).find((a) => a.is_default) || (data || [])[0];
                    if (def) { lat = def.lat; lng = def.lng; pincode = def.pincode; }
                } catch { /* token expired / not a customer — bail silently */ return; }
                if (lat == null && pincode == null) return;

                const params = new URLSearchParams();
                if (lat != null) { params.set("lat", lat); params.set("lng", lng); }
                if (pincode) params.set("pincode", pincode);
                const { data } = await api.get(`/zone-status?${params.toString()}`);
                if (!cancelled && data && data.serviceable === false) {
                    setState({ show: true, msg: data.message });
                }
            } catch { /* ignore */ }
        })();
        return () => { cancelled = true; };
    }, []);

    if (!state.show || dismissed) return null;

    return (
        // In the page flow at the top of the customer shell (App.js mounts it before the route), not floating: a
        // fixed banner sat over every page's header — the address row, search bar, checkout title and shop hero.
        <div data-testid="zone-offline-banner"
            className="mx-3 mt-3 bg-amber-50 border border-amber-300 text-amber-900 rounded-2xl px-4 py-3 shadow-sm flex items-start gap-3 animate-in slide-in-from-top">
            <AlertTriangle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm font-semibold flex-1">{state.msg}</p>
            <button data-testid="zone-banner-dismiss" onClick={() => { setDismissed(true); sessionStorage.setItem("bizil_zone_dismiss", "1"); }}
                className="text-amber-600 p-1 -m-1"><X size={16} /></button>
        </div>
    );
}
